import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Manages an aria-live region for screen reader announcements.
 * Returns an `announce(message, politeness?)` function and the message/politeness
 * to render in a visually-hidden element.
 *
 * Setting the same string twice in a row would be ignored by some screen readers,
 * so we briefly clear and re-set to guarantee re-announcement.
 */
export function useLiveRegion() {
  const [message, setMessage] = useState("");
  const [politeness, setPoliteness] = useState<"polite" | "assertive">("polite");
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const announce = useCallback((msg: string, level: "polite" | "assertive" = "polite") => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    setPoliteness(level);
    setMessage(""); // clear first to force re-announcement
    timeoutRef.current = window.setTimeout(() => setMessage(msg), 50);
  }, []);

  return { message, politeness, announce };
}
